/**
 * Shopify cart handoff — API_CONTRACT.md "Cart handoff (SPA-side, after /checkout 200)".
 *
 * After POST /checkout returns the authoritative cart items, the SPA must:
 *   1. GET /cart.js and zero out every existing line that carries
 *      `properties._rpl == "1"` (a previous portal handoff) via POST /cart/change.js
 *      — one call per line, serialized (Shopify cart mutations are not concurrent-safe).
 *   2. POST /cart/add.js with the new items in batches of ≤ 50.
 *   3. window.location.href = "/checkout".
 *
 * The pure parts (line-key extraction, batching) are exported for unit tests; the
 * runner takes injectable fetch/navigate so the whole dance is testable too.
 */

import type { CartItem } from './types';

/** One line of a Shopify GET /cart.js response (only the fields we read). */
export interface ShopifyCartLine {
  /** Stable line key ("<variantId>:<hash>") — safe id for /cart/change.js. */
  key: string;
  quantity: number;
  properties?: Record<string, unknown> | null;
}

export interface ShopifyCartJson {
  items?: ShopifyCartLine[];
}

/** Max items per POST /cart/add.js call (Shopify limit; contract says ≤ 50). */
export const ADD_BATCH_MAX = 50;

/** Keys of cart lines placed by a previous portal handoff (`_rpl == "1"`). */
export function rplLineKeys(cart: ShopifyCartJson): string[] {
  return (cart.items ?? [])
    .filter((line) => String(line.properties?.['_rpl'] ?? '') === '1')
    .map((line) => line.key);
}

/** Split `items` into ordered batches of at most `max` (default 50). */
export function buildAddBatches<T>(items: readonly T[], max = ADD_BATCH_MAX): T[][] {
  if (max < 1) throw new Error(`Batch size must be >= 1, got ${max}`);
  const batches: T[][] = [];
  for (let i = 0; i < items.length; i += max) {
    batches.push(items.slice(i, i + max));
  }
  return batches;
}

export interface CartHandoffDeps {
  /** Injectable fetch (tests). Defaults to window fetch, same-origin. */
  fetchFn?: typeof fetch;
  /** Injectable navigation (tests). Defaults to `window.location.href = url`. */
  navigate?: (url: string) => void;
}

/** Thrown when any step of the handoff fails — message is user-presentable. */
export class CartHandoffError extends Error {
  constructor(
    readonly step: 'read_cart' | 'clear_lines' | 'add_items' | 'set_note',
    message: string,
  ) {
    super(message);
    this.name = 'CartHandoffError';
  }
}

async function postJson(fetchFn: typeof fetch, url: string, body: unknown): Promise<Response> {
  return fetchFn(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
}

/**
 * Run the full 3-step handoff. Resolves only in tests — in the browser the final
 * navigate() unloads the page. Throws CartHandoffError on any failed step.
 */
export async function runCartHandoff(
  items: readonly CartItem[],
  deps: CartHandoffDeps = {},
  /** Customer instructions -> Shopify cart note (arrives on the order webhook
   * as order.note; ALWAYS written, '' clears a stale note from a prior visit). */
  orderNote = '',
): Promise<void> {
  const fetchFn = deps.fetchFn ?? window.fetch.bind(window);
  const navigate =
    deps.navigate ??
    ((url: string) => {
      window.location.href = url;
    });

  // 1. Read the live cart and zero out stale portal lines.
  let cart: ShopifyCartJson;
  try {
    const res = await fetchFn('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`GET /cart.js → ${res.status}`);
    cart = (await res.json()) as ShopifyCartJson;
  } catch {
    throw new CartHandoffError('read_cart', "We couldn't reach the store cart.");
  }

  for (const key of rplLineKeys(cart)) {
    const res = await postJson(fetchFn, '/cart/change.js', { id: key, quantity: 0 }).catch(
      () => null,
    );
    if (!res || !res.ok) {
      throw new CartHandoffError('clear_lines', "We couldn't refresh your existing cart.");
    }
  }

  // 2. Add the new items in batches of ≤ 50, in order.
  for (const batch of buildAddBatches(items)) {
    const res = await postJson(fetchFn, '/cart/add.js', { items: batch }).catch(() => null);
    if (!res || !res.ok) {
      throw new CartHandoffError('add_items', "We couldn't add your prints to the store cart.");
    }
  }

  // 3. Cart note (customer instructions). Non-fatal is NOT ok here — the lab
  // relies on these instructions — so a failure blocks like the other steps.
  const noteRes = await postJson(fetchFn, '/cart/update.js', { note: orderNote }).catch(() => null);
  if (!noteRes || !noteRes.ok) {
    throw new CartHandoffError('set_note', "We couldn't attach your instructions to the order.");
  }

  // 4. Off to Shopify's native checkout.
  navigate('/checkout');
}
